import { FiTrash, FiUpload, FiRefreshCw, FiPaperclip } from "react-icons/fi";
import { Input, Box, Image, Flex, Text } from "@chakra-ui/react";

import {
  ChangeEvent,
  ChangeEventHandler,
  MouseEventHandler,
  useRef,
  useState
} from "react";
import truncate from "lodash/truncate";

import {
  Button,
  ButtonProps,
  ButtonGroup,
  AspectRatio
} from "@/storybook-components";

const MAX_LENGTH = 30;

export const DEFAULT_ACCEPTED_FILE_TYPE =
  ".jpg,.jpeg,.gif,.png,.bmp,.zip,.xml,.mp4,.avi,.mpeg,.mov,.flv,.mp3,.wav,.x-wav,.pdf,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.rtf";

export interface FileUploadProps
  extends Pick<
    ButtonProps,
    | "id"
    | "size"
    | "onBlur"
    | "children"
    | "isDisabled"
    | "loadingText"
    | "className"
    | "colorScheme"
    | "aria-label"
  > {
  fileName: string;
  fileSrc: string;
  fileContentType: string;
  accept: string;
  helpText: string;
  deleteText: string;
  replaceText: string;
  isDeleteDisabled: boolean;
  isReplaceDisabled: boolean;
  onUpload: ChangeEventHandler<HTMLInputElement>;
  onDelete: MouseEventHandler<HTMLElement>;
}

function FileUpload({
  id,
  size,
  accept,
  onBlur,
  children,
  onUpload,
  onDelete,
  isDisabled,
  loadingText,
  className,
  colorScheme,
  helpText,
  fileSrc,
  fileName,
  fileContentType,
  deleteText,
  replaceText,
  isDeleteDisabled,
  isReplaceDisabled,
  "aria-label": ariaLabel
}: Partial<FileUploadProps>) {
  const [isUploading, setIsUploading] = useState(false);
  const inputFileNode = useRef<HTMLInputElement>(null);

  const handleSelect = () => {
    if (inputFileNode && inputFileNode?.current) inputFileNode.current.click();
  };

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true);
    onUpload && (await onUpload(e));
    setIsUploading(false);
  };

  const displayUploadedFile = () => {
    return (
      <Box maxWidth={{ lg: "24rem" }}>
        {fileContentType &&
          (/^image\//.test(fileContentType) ||
            /(gif|jpg|jpeg|tiff|png|bmp)$/i.test(fileContentType)) && (
            <AspectRatio ratio={16 / 9}>
              <Image
                height={200}
                fallback={<Text>...</Text>}
                alt={fileName}
                borderRadius={8}
                data-testid="fileUpload-image"
                src={fileSrc}
              />
            </AspectRatio>
          )}
        <Flex mt={2} fontSize={size} alignItems="center">
          <FiPaperclip /> &nbsp;
          {truncate(fileName, { length: MAX_LENGTH })}
        </Flex>
        <Flex
          mt={4}
          as={ButtonGroup}
          variant="ghost"
          justifyContent="space-between"
        >
          <Button
            variant="tertiary"
            size="sm"
            leftIcon={<FiRefreshCw />}
            onClick={handleSelect}
            data-testid="fileUpload-replaceButton"
            isDisabled={isReplaceDisabled}
          >
            {replaceText}
          </Button>
          <Input
            id={id}
            type="file"
            display="none"
            aria-label={ariaLabel}
            ref={inputFileNode}
            onChange={handleUpload}
            accept={accept || DEFAULT_ACCEPTED_FILE_TYPE}
          />
          <Button
            variant="tertiary"
            size="sm"
            leftIcon={<FiTrash />}
            onClick={onDelete}
            data-testid="fileUpload-deleteButton"
            isDisabled={isDeleteDisabled}
          >
            {deleteText}
          </Button>
        </Flex>
      </Box>
    );
  };

  return fileSrc && !isUploading ? (
    displayUploadedFile()
  ) : (
    <Flex
      flexDirection={{ base: "column", md: "row" }}
      alignItems={{ lg: "center" }}
    >
      <Button
        variant="secondary"
        id={id}
        size={size}
        display="flex"
        onBlur={onBlur}
        onClick={handleSelect}
        isDisabled={isDisabled}
        isLoading={isUploading}
        data-testid="fileUpload-button"
        loadingText={loadingText}
        className={className}
        colorScheme={colorScheme}
        aria-label={ariaLabel}
        leftIcon={<FiUpload />}
      >
        {children}
      </Button>
      <Input
        id={id}
        type="file"
        display="none"
        aria-label={ariaLabel}
        ref={inputFileNode}
        onChange={handleUpload}
        accept={accept || DEFAULT_ACCEPTED_FILE_TYPE}
      />
      {helpText && (
        <Text
          id="file-upload"
          ml={{ lg: 4 }}
          dangerouslySetInnerHTML={{ __html: helpText }}
        />
      )}
    </Flex>
  );
}

export { FileUpload };
